// AskIAM UI Theme - MUI Theme Configuration
// Reusable theme for Material UI projects

import { createTheme, type PaletteMode } from '@mui/material/styles';
import { palette } from './palette';
import { typography } from './typography';
import { layout } from './layout';

export function createAppTheme(mode: PaletteMode) {
  // The colour tokens are solved against light surfaces, where "safe" means
  // darker. On a dark surface that is exactly backwards, so applying them to
  // both modes would have made dark worse every time light improved - silently,
  // because dark has never been scanned. Dark keeps the values it already had
  // until it can be measured; see the SCOPE note in palette.ts.
  const isDark = mode === 'dark';
  const tone = isDark ? palette.darkMode : palette;
  const focusBoundary = {
    outline: `3px solid ${palette.focus.border}`,
    outlineOffset: '2px',
    boxShadow: `0 0 0 2px ${palette.focus.halo}, 0 0 0 7px ${palette.focus.halo}`,
    // Focus must not fade in with a control's hover/elevation transition.
    transition: 'none',
    '@media (forced-colors: active)': {
      outlineColor: 'Highlight',
      boxShadow: 'none',
    },
  };
  const insetFocusBoundary = {
    ...focusBoundary,
    outlineOffset: '-6px',
    boxShadow: `inset 0 0 0 7px ${palette.focus.halo}`,
  };

  return createTheme({
    palette: {
      mode,
      primary: tone.primary,
      secondary: tone.secondary,
      error: tone.error,
      warning: tone.warning,
      success: tone.success,
      info: tone.info,
      ...(isDark
        ? {}
        : {
            background: palette.background,
            text: palette.text,
            divider: palette.divider,
          }),
    },
    typography: {
      fontFamily: typography.fontFamily,
      h1: typography.h1,
      h2: typography.h2,
      h3: typography.h3,
      h4: typography.h4,
      body1: typography.body1,
      body2: typography.body2,
      caption: typography.caption,
      button: typography.button,
    },
    shape: {
      borderRadius: layout.borderRadius.small,
    },
    // A leaner header (48px, not MUI's 56/64 default) at every breakpoint,
    // matching the flat, lean style used across other internal business
    // tools. This is the single source GuestDetailPanel's drawer
    // reads via theme.mixins.toolbar to offset itself below the AppBar - keep
    // it matching layout.headerHeight rather than overriding Header's Toolbar
    // height locally, or that drawer clips its own content again.
    mixins: {
      toolbar: {
        minHeight: layout.headerHeight,
        '@media (min-width:0px) and (orientation: landscape)': { minHeight: layout.headerHeight },
        '@media (min-width:600px)': { minHeight: layout.headerHeight },
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Also prevent the halo from lingering after blur when elevation normally animates.
          'body :is(a[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"], .MuiButtonBase-root, .MuiInputBase-root, .MuiPickersInputBase-root, .MuiAutocomplete-option)': {
            transitionProperty: 'background-color, color, border-color, opacity, transform',
            scrollMargin: '8px',
          },
          // Use :focus, not only :focus-visible: pointer and restored focus need the same boundary.
          // Keep each :is alternative at one class of specificity so the inset rules below win.
          'body :is(:where(a)[href], button, input, select, textarea, summary, [tabindex]:where(:not([tabindex="-1"])), [role="button"], [role="option"], [role="menuitem"], [role="tab"], [contenteditable="true"]):focus': focusBoundary,
          'body :is(button, a[href], [role="button"]):focus': { opacity: 1 },
          // Loading/inactive panels may dim content, but not a control that still holds focus.
          'body :is(.MuiBox-root, .MuiStack-root, .MuiPaper-root, .MuiTableContainer-root, .MuiTableRow-root):focus-within': { opacity: 1 },
          // Transparent/inner inputs cannot show a useful ring; paint their visible control instead.
          'body .MuiInputBase-root:has(.MuiInputBase-input:focus), body .MuiPickersInputBase-root:has([contenteditable="true"]:focus)': insetFocusBoundary,
          // The transformed adornment otherwise paints below the floating input label.
          'body .MuiAutocomplete-endAdornment:focus-within': { zIndex: 2 },
          'body .MuiButtonBase-root:has(> input:focus)': insetFocusBoundary,
          'body :is(.MuiInputBase-input, .MuiSelect-select, .MuiPickersSectionList-sectionContent):focus, body .MuiButtonBase-root > input:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
          // These controls touch scroll/clipping edges. An inset ring stays inside their viewport.
          'body :is(.MuiCardActionArea-root, .MuiTab-root, .MuiMenuItem-root, .MuiAccordionSummary-root, .MuiTableRow-root, .MuiTableContainer-root, .MuiPickersDay-root):focus': insetFocusBoundary,
          // Leave room for the sidebar's 3px current-page border and a white outer halo.
          'body .MuiListItemButton-root:focus': {
            ...insetFocusBoundary,
            outlineOffset: '-9px',
            boxShadow: `inset 0 0 0 10px ${palette.focus.halo}`,
          },
          // Autocomplete keeps DOM focus in the input while arrows move its active option.
          'body .MuiAutocomplete-option.Mui-focusVisible': insetFocusBoundary,
          'body .react-flow .react-flow__node:focus': focusBoundary,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: layout.borderRadius.small,
            textTransform: 'none',
            fontWeight: 600,
          },
          containedPrimary: {
            // tone, not palette: primary.dark is now the AAA #004274, which is
            // a light-mode value. Reading it directly here would have let the
            // one override in this file escape the dark-mode pin and darken
            // dark's hover state - the exact regression palette.darkMode exists
            // to prevent. Any future override that reads a severity colour must
            // read it from tone for the same reason.
            '&:hover': {
              backgroundColor: tone.primary.dark,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: layout.borderRadius.medium,
            boxShadow: layout.shadow.card,
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          // MUI's default variantMapping renders subtitle1 and subtitle2 as <h6>.
          // They are used all over this app for field labels, accordion summaries
          // and chart captions - "Requestor", "Conversation", "Workflow Runs" -
          // none of which are headings. Each one became a stray h6 in the outline,
          // which is why axe reported broken heading order on pages that look
          // perfectly ordered on screen (axe: heading-order). Mapping them to a
          // div removes the false headings without changing a pixel; anything
          // that really is a heading says so with an explicit variant or
          // component. div rather than p because these frequently wrap chips and
          // icons, which are not valid inside a paragraph.
          variantMapping: {
            subtitle1: 'div',
            subtitle2: 'div',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          // MUI ships `.MuiAlert-message { overflow: auto }`, which turns every
          // alert into a scroll container. When the text is long enough to
          // overflow, part of the message is hidden behind a scrollbar that a
          // keyboard user cannot reach - the alert has nothing focusable inside
          // it to scroll from (axe: scrollable-region-focusable, WCAG 2.1.1).
          // The alert has no height of its own, so letting it grow shows the
          // whole message instead of clipping it; `anywhere` keeps long
          // unbroken strings - stack traces, GUIDs, URLs - inside the box
          // rather than spilling out now that nothing clips them.
          message: {
            overflow: 'visible',
            overflowWrap: 'anywhere',
          },
          // MUI derives the standard and outlined Alert's text from
          // palette[severity].light, not .main: text is darken(light, 0.6) and
          // the standard tint is lighten(light, 0.9). That pairing lands at
          // 6.07-6.30, just short of AAA. Darkening `light` itself would repaint
          // every alert's tint and anything else consuming the token, so these
          // keep the same hue and only push the darken coefficient to ~0.645,
          // solved to >=7.25 against both the tint and white (outlined alerts
          // sit on paper). Measured: error 7.35/7.45, warning 7.33/7.40,
          // success 7.29/7.37, info 7.28/7.37.
          //
          // Light mode only. These are flat literals solved against near-white
          // surfaces; applied in dark mode they would paint dark grey text on a
          // dark alert. Dark keeps MUI's own derivation until it is measured -
          // the same reason palette.darkMode exists.
          // TODO: re-solve for dark once a dark scan has actually run.
          ...(isDark
            ? {}
            : {
                standardError: { color: '#5b5354' },
                outlinedError: { color: '#5b5354' },
                standardWarning: { color: '#59554e' },
                outlinedWarning: { color: '#59554e' },
                standardSuccess: { color: '#525753' },
                outlinedSuccess: { color: '#525753' },
                standardInfo: { color: '#50575a' },
                outlinedInfo: { color: '#50575a' },
              }),
        },
      },
      MuiAvatar: {
        styleOverrides: {
          // MUI draws initials in `background.default` (#f5f5f5) rather than
          // white, so an avatar's contrast silently depends on the page colour.
          // White is what the tinted avatar colours were chosen against.
          root: {
            color: '#ffffff',
          },
          // The fallback tint is grey[400] (#bdbdbd), which scores 1.7 against
          // its own initials - the worst ratio in the app. grey[700] (#616161)
          // fixed that at 6.19; one step further keeps the "no colour assigned"
          // look and reaches 7.00.
          //
          // Deliberately NOT gated on light mode, unlike every other override in
          // this file. Those gates exist because darkening raises contrast on a
          // light surface and lowers it on a dark one - but this colour is never
          // read against the page, only against its own white initials, so it
          // carries in both modes. MUI's dark default here is grey[600]
          // (#757575), which scores 4.61 - below AAA and barely above AA. Gating
          // this to light mode would hand dark mode back that 4.61.
          //
          // The trade-off, stated rather than hidden: the avatar's circle against
          // the dark page drops from 4.07 to 2.67. In dark mode you cannot have
          // both - a background light enough to clear 3:1 against #121212 needs
          // luminance >= 0.117, and 7:1 initials on it would need a text
          // luminance above 1.0 (impossible) or below zero (also impossible).
          // Readable initials win over a crisper circle: 1.4.11 covers graphics
          // needed to understand content, and the content here is the initials.
          colorDefault: {
            backgroundColor: '#595959',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          // MUI's unselected label is rgba(0,0,0,0.54), which resolves to
          // #717171 on the page background: 4.47, just under the bar. Nothing
          // about the design needs it that light. Dark mode is user-reachable
          // (App.tsx toggles it from the header), so this cannot be a flat
          // hex - a dark grey on a dark surface would be worse than what it
          // replaced. Dark mode keeps the palette's own secondary text.
          root: ({ theme }) => ({
            color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#535353',
          }),
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
          fullWidth: true,
        },
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
          fullWidth: true,
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiTablePagination: {
        defaultProps: {
          // The global MuiSelect `fullWidth` default above leaks into the pager's rows-per-page
          // Select, whose own styles pin it to `flex-shrink: 0`. A 100%-wide, non-shrinkable flex
          // item fills the toolbar and pushes the "1-10 of 47" label and the prev/next buttons
          // outside it; the toolbar's `overflow: auto` then hides them behind a scrollbar nobody
          // notices. Opt the pager out so it sizes to its content. See AB#104.
          SelectProps: {
            fullWidth: false,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: layout.borderRadius.medium,
            boxShadow: layout.shadow.card,
          },
        },
      },
    },
  });
}

const theme = createAppTheme('light');
export default theme;
